import React, { useEffect, useState } from "react";

import homePagePayload from "../../config/homePayload";
import homePageAPI from "../../api/homePageApi";

import Banner from "./Banner";
import CouponFlash from "./CouponFlash";
import CategoryCard from "../categories/Card";
import Testimonials from "../testimonials/Card";
import Blog from "../blogs/Card";
import Product from "../products/Card";
import { motion, AnimatePresence } from "framer-motion";
import { MoveRight, Calendar } from "lucide-react";

import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "./Shared/CustomSwiper";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import MiddleSection from "./MiddleSection";
import CtaBanner from "./CtaBanner";

function Home() {
  const CACHE_TIME = 180000; // 3 minutes

  const [banners, setBanners] = useState([]);
  const [middleBanners, setMiddleBanners] = useState([]);
  const [topBanners, setTopBanners] = useState([]);

  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [products, setProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [activeId, setActiveId] = useState(testimonials?.[0]?.id || null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Set the active ID based on the card currently in center
            setActiveId(Number(entry.target.getAttribute("data-id")));
          }
        });
      },
      {
        root: null,
        rootMargin: "-45% 0px -45% 0px", // Trigger only when element is in the exact MIDDLE 10% of screen
        threshold: 0,
      },
    );

    const cards = document.querySelectorAll(".editorial-card");
    cards.forEach((card) => observer.observe(card));

    return () => cards.forEach((card) => observer.unobserve(card));
  }, []);

  useEffect(() => {
    fetchHomePageDetails();
  }, []);

  const fetchHomePageDetails = async () => {
    const res = await homePageAPI.homePage(homePagePayload);

    if (res.code === 200) {
      const result = res.data;

      const allBanners = result?.banners || [];

      setTopBanners(allBanners.filter((b) => b.position === "top"));
      setMiddleBanners(allBanners.filter((b) => b.position === "middle"));
      setCoupons(result?.coupons);
      setCategories(result?.categories);
      setTestimonials(result?.testimonials);
      setBlogs(result?.blogs);
      setProducts(result?.products);
      setTestimonials(result?.testimonials);
    }
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [activeVariant, setActiveVariant] = useState(1);

  return (
    <>
      <section className="accordion-hero">
        <div className="pillars-container">
          {products.slice(0, 3).map((product, index) => {
            const isActive = activeVariant === index;

            return (
              <motion.div
                key={product.id}
                className={`pillar ${isActive ? "active" : ""}`}
                onClick={() => setActiveVariant(index)}
                // Animate Flex grow
                layout
                style={{ flex: isActive ? 3.5 : 1 }}
                transition={{ type: "spring", stiffness: 120, damping: 20 }}
              >
                {/* Background Image */}
                <img
                  src="../../../images/topbanner.png"
                  className="pillar-bg"
                />
                <div className="pillar-overlay"></div>

                {/* --- 1. COLLAPSED VIEW (Vertical) --- */}
                <AnimatePresence>
                  {!isActive && (
                    <motion.div
                      className="collapsed-content"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* <span className="vertical-num">{`0${index + 1}`}</span> */}
                      <span className="vertical-text">{product.title}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* --- 2. EXPANDED VIEW (Content) --- */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      className="expanded-content"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.2 } }}
                      exit={{ opacity: 0 }}
                    >
                      <div className="product-showcase">
                        {/* Floating Product Image */}
                        <motion.img
                          src={product.thumbnail}
                          alt="Product"
                          className="product-img-float"
                          initial={{ y: 100, opacity: 0, scale: 0.8 }}
                          animate={{ y: 0, opacity: 1, scale: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 100,
                            delay: 0.2,
                          }}
                        />

                        {/* Text Details */}
                        <div className="text-area">
                          <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                          >
                            {product.title}
                          </motion.h2>

                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                          >
                            {product.shortDescription}
                          </motion.p>

                          <Link to="/shop">
                            <motion.div
                              className="glass-btn "
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.6 }}
                            >
                              View Details{" "}
                              <ArrowRight size={16} style={{ marginLeft: 5 }} />
                            </motion.div>
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </section>
      {/* coupon listing */}
      {coupons && coupons.length > 0 && (
        <div className="marquee-sale marquee-border font-main">
          <div className="marquee-wrapper">
            <div className="initial-child-container">
              {coupons.map((coupon, index) => (
                <React.Fragment key={index}>
                  <CouponFlash item={coupon} />
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* category listing */}
      {categories && categories.length > 0 && (
        <section className="shutter-section">
          <div className="container">
            {/* Simple, Clean Header */}
            <div className="row mb-5 align-items-end">
              <div className="col-lg-12 text-start">
                {/* <span className="shutter-subtitle">Botanical/ Series</span> */}
                <h2 className="shutter-heading">
                  <span>Trending Products</span>
                </h2>
                {/* <div className="shutter-line"></div> */}
              </div>
            </div>

            {/* The Grid */}
            <div className="row g-4">
              {categories.map((category, index) => (
                <div key={index} className="col-lg-6 col-md-6">
                  <CategoryCard item={category} />
                  {/* Border effect */}
                  <div className="border-trace"></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      <MiddleSection />
      {products && products.length > 0 && (
        <div className="compact-section">
          <div className="container ">
            <div className="">
              {/* <div className="">
                <h4>Our Products</h4>
                  <h2 className="compact-title">Essential <span>Care</span></h2>
              </div> */}
              <div className="text-center mb-5">
                {/* <span className="compact-sub">Daily Rituals</span> */}
                <h2 className="compact-title text-start">
                  Feature <span>Products</span>
                </h2>
              </div>
              <div className="row  g-4 ">
                {products.map((product, index) => (
                  <div
                    className="col-lg-3 col-sm-6 col-xs-6 mb-3"
                    key={[product._id || index]}
                  >
                    <Product item={product} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {middleBanners &&
        middleBanners.length > 0 &&
        middleBanners.map((banner, index) => (
          <CtaBanner key={index} banner={banner} />
        ))}
      {testimonials && testimonials.length > 0 && (
        <section className="editorial-section">
          <div className="container">
            <div className="text-center mb-5">
              {/* <span className="compact-sub">Daily Rituals</span> */}
              <h2 className="compact-title text-start">
                Customer <span>Reviews</span>
              </h2>
            </div>
            <div className="row">
              <div className="col-lg-5 d-none d-lg-block">
                <div className="sticky-frame">
                  <div className="visual-card">
                    <img src="../../../images/bannesides.png" alt="Radiant Skin" />
                    <div className="visual-overlay">
                    
                      <h3>
                        Real Stories.
                        <br />
                        Real Results.
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6 offset-lg-1">
                {/* <div className="d-lg-none text-center mb-5">
                  <h2 className="mobile-title">Community Love</h2>
                  <p className="mobile-sub">Rated 4.9/5 by 2000+ Customers</p>
                </div> */}

                <div className="reviews-timeline">
                  {/* Vertical Line Connector */}
                  <div className="timeline-line"></div>

                  {testimonials.map((testimonial, index) => (
                    <div key={index}>
                      <div
                        key={testimonial.id}
                        data-id={testimonial.id}
                        className={`editorial-card ${activeId === testimonial.id ? "card-active" : ""}`}
                      >
                        {/* Timeline Dot */}
                        <div
                          className={`timeline-dot ${activeId === testimonial.id ? "dot-active" : ""}`}
                        ></div>
                        <Testimonials item={testimonial} />
                      </div>
                    </div>
                  ))}
                  <div style={{ height: "20vh" }}   className="d-none d-lg-block"></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      {blogs && blogs.length > 0 && (
        <section className="beauty-blog-section">
          <div className="container">
            {/* HEADER */}
            <div className="d-flex justify-content-between  align-items-center">
              {/* <h4 className="text-white">Latest Blogs</h4> */}
              <h2 className="compact-title mb-4">
                Latest <span>Blogs</span>
              </h2>
              {/* <div className="text-center mb-5">
                    <span className="compact-sub">Daily Rituals</span>
                    
                </div> */}
              <Link to="/blog" className="custom-link-blog">
                <span className="custom-our-product-title-span text-black">
                  View All <MoveRight />
                </span>
              </Link>
            </div>

            <Swiper
              modules={[Navigation, Autoplay, Pagination]}
              slidesPerView={3}
              spaceBetween={20}
              pagination={{ clickable: true }}
              autoplay={{
                delay: 2500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              breakpoints={{
                0: { slidesPerView: 1, spaceBetween: 10 },
                576: { slidesPerView: 1, spaceBetween: 15 },
                768: { slidesPerView: 2, spaceBetween: 20 },
                1024: { slidesPerView: 3, spaceBetween: 20 },
              }}
            >
              {blogs.slice(0, 6).map((blog, index) => {
                const dateObj = new Date(blog.date);

                const day = dateObj.getDate();
                const month = dateObj.toLocaleString("default", {
                  month: "short",
                });
                const year = dateObj.getFullYear();

                return (
                  <SwiperSlide key={index}>
                    <div className="blog-item">
                      {/* LEFT DATE */}
                      <div className="blog-date-vertical">
                        <div className="date-icon">
                          <Calendar size={24} />
                        </div>

                        <div className="date-box">
                          <span className="month-day">
                            {month} {day}
                          </span>
                          <span className="year">{year}</span>
                        </div>
                      </div>

                      {/* RIGHT CONTENT */}
                      <div className="blog-content">
                        <div className="blog-img">
                          <img src={blog.image} alt={blog.title} />
                        </div>

                        <h4>{blog.title}</h4>

                        <p className="short-desc">
                          {blog.shortDescription?.slice(0, 90)}...
                        </p>

                        <div className="divider"></div>

                        <div className="blog-bottom">
                          <span>{blog.author}</span>

                          <Link to={`/blog/${blog.slug}`} className="read-btn">
                            Read More →
                          </Link>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </section>
      )}
    </>
  );
}
export default Home;
